import { useEffect, useRef, useState, useCallback } from "react";
import { wsUrl } from "../api";
import type { GameState } from "@/types/poker";

interface Options {
  token?: string | null;
  room?: string | null;
  onState?: (state: GameState) => void;
  onError?: (error: Event) => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 1000;

export const usePokerWS = ({ token, room, onState, onError }: Options) => {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const heartRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stabilize callbacks with refs to avoid reconnecting on every render
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const cleanup = useCallback(() => {
    if (heartRef.current) {
      clearInterval(heartRef.current);
      heartRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!token || !room) return;

    cleanup();

    const url = wsUrl(room, token);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectRef.current = 0;
      if (heartRef.current) clearInterval(heartRef.current);
      heartRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send("ping");
        }
      }, 25000);
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed.type === "state" && parsed.payload) {
          onStateRef.current?.(parsed.payload);
        }
      } catch (e) {
        console.error("[usePokerWS] Failed to parse message:", e);
      }
    };

    ws.onerror = (event: Event) => {
      console.error("[usePokerWS] WebSocket error:", event);
      setConnected(false);
      onErrorRef.current?.(event);
    };

    ws.onclose = (event: CloseEvent) => {
      setConnected(false);
      if (heartRef.current) {
        clearInterval(heartRef.current);
        heartRef.current = null;
      }

      // Don't reconnect if closed intentionally (code 4003 = invalid token)
      if (event.code === 4003) return;

      // Exponential backoff reconnect
      if (reconnectRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = BASE_RECONNECT_DELAY * Math.pow(2, reconnectRef.current);
        reconnectRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };
  }, [token, room, cleanup]);

  useEffect(() => {
    connect();
    return cleanup;
  }, [connect, cleanup]);

  return { connected };
};
